import {
  VStack,
  Text,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Flex,
  Button,
  Switch,
} from "@chakra-ui/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

export interface CreateListFormData {
  name: string;
  description?: string;
  is_public: boolean;
}

interface CreateListFormProps {
  onCreateList: (listData: CreateListFormData) => void;
  onCancel: () => void;
  isCreating: boolean;
}

// The form's own title is intentionally NOT rendered here — the parent
// shell (AddToListButton's popover / drawer header) owns the title
// "Create new <kind> list" along with the back affordance, so rendering
// it inside the body would double up. listType still flows in via the
// shell's header copy.
export function CreateListForm({
  onCreateList,
  onCancel,
  isCreating,
}: CreateListFormProps) {
  const [t] = useTranslation();
  const [newListName, setNewListName] = useState("");
  const [newListDescription, setNewListDescription] = useState("");
  // Default to public — matches the wizard's default and is the choice
  // most users make. A new list created from a comic / release detail
  // surface usually exists to be shared; private is the deliberate opt-out.
  const [isPublic, setIsPublic] = useState(true);

  const handleCreate = () => {
    if (newListName.trim()) {
      onCreateList({
        name: newListName.trim(),
        description: newListDescription.trim() || undefined,
        is_public: isPublic,
      });
    }
  };

  const handleCancel = () => {
    setNewListName("");
    setNewListDescription("");
    setIsPublic(true);
    onCancel();
  };

  return (
    <VStack spacing={3} p={3} align="stretch" width="100%">
      <FormControl isRequired>
        <FormLabel fontSize="sm" color="neutral.400">
          {t("components.lists.createListForm.nameLabel")}
        </FormLabel>
        <Input
          value={newListName}
          onChange={(e) => setNewListName(e.target.value)}
          placeholder={t("components.lists.createListForm.namePlaceholder")}
          bg="transparent.white.10"
          border="1px solid"
          borderColor="transparent.white.20"
          color="neutral.200"
          _placeholder={{ color: "neutral.500" }}
          _focus={{
            borderColor: "blaze.300",
            bg: "transparent.white.20",
          }}
          autoFocus
        />
      </FormControl>
      <FormControl>
        <FormLabel fontSize="sm" color="neutral.400">
          {t("components.lists.createListForm.descriptionLabel")}
        </FormLabel>
        <Textarea
          value={newListDescription}
          onChange={(e) => setNewListDescription(e.target.value)}
          placeholder={t(
            "components.lists.createListForm.descriptionPlaceholder",
          )}
          bg="transparent.white.10"
          border="1px solid"
          borderColor="transparent.white.20"
          color="neutral.200"
          _placeholder={{ color: "neutral.500" }}
          _focus={{
            borderColor: "blaze.300",
            bg: "transparent.white.20",
          }}
          resize="vertical"
          minH="56px"
        />
      </FormControl>
      {/*
        Privacy row — same shape and copy as the shelf wizard's step-3
        PrivacyRow so the inline path through AddToListButton stays at
        feature parity with the full create flow. Default state and
        helper-text wording are kept identical on purpose.
      */}
      <FormControl
        display="grid"
        gridTemplateColumns="auto 1fr"
        gap="12px"
        alignItems="center"
        p="10px 12px"
        border="1px solid"
        borderColor="transparent.white.10"
        borderRadius="10px"
        bg="transparent.white.05"
      >
        <Switch
          id="add-to-list-create-form-privacy"
          size="sm"
          variant="brandSwitch"
          isChecked={isPublic}
          onChange={(e) => setIsPublic(e.target.checked)}
          isDisabled={isCreating}
        />
        <FormLabel
          htmlFor="add-to-list-create-form-privacy"
          m={0}
          cursor="pointer"
        >
          <Text fontSize="13px" fontWeight={500} color="dune.dune">
            {isPublic
              ? t("components.lists.createListForm.privacyPublicLabel")
              : t("components.lists.createListForm.privacyPrivateLabel")}
          </Text>
          <Text fontSize="11px" color="dune.700" mt="2px">
            {isPublic
              ? t("components.lists.createListForm.privacyPublicHelper")
              : t("components.lists.createListForm.privacyPrivateHelper")}
          </Text>
        </FormLabel>
      </FormControl>
      <Flex gap={2} justify="flex-end">
        <Button
          variant="secondary"
          size="sm"
          onClick={handleCancel}
          isDisabled={isCreating}
        >
          {t("components.lists.createListForm.cancel")}
        </Button>
        <Button
          variant="primary"
          size="sm"
          onClick={handleCreate}
          isDisabled={!newListName.trim() || isCreating}
          isLoading={isCreating}
        >
          {t("components.lists.createListForm.submit")}
        </Button>
      </Flex>
    </VStack>
  );
}
